/**
 * Pestaña "Facturación": lo que se le cobra al cliente en el mes.
 *
 * La regla de toda la pantalla: **donde falta un insumo se pinta un guion y se
 * dice qué falta, nunca un cero.** Un total incompleto pintado como número se
 * lee como un total completo más barato, y aquí eso es una factura mal hecha.
 *
 * **Lo que se cobra y lo que sólo se informa van separados.** La llave de IA la
 * designó el cliente, así que ese consumo ya se lo cobra el proveedor a él:
 * meterlo en la suma sería cobrárselo dos veces. Se muestra —lo necesita para
 * cuadrar su propia factura— pero fuera del total, y dicho con todas sus letras.
 *
 * Los dos bloques llevan su alcance escrito, y no es decoración: el de IA sólo
 * cuenta el gasto que llevó un usuario (los procesos automáticos van sin
 * usuario y no se graban), y el del servidor no incluye IP fija ni respaldos.
 * Sin esos rótulos la página promete una factura completa que no es.
 */
import { useState } from "react";

import {
  useBillingMargin,
  useBillingSummary,
  useSetBillingMargin,
  type BillingSummary,
} from "@/hooks/useBilling";
import { fechaCorta } from "@/hooks/useFx";
import { formatBytes, formatMoneda, formatMxn, formatNumber } from "@/lib/usageHelpers";

function mesActual(): string {
  const hoy = new Date();
  return `${hoy.getUTCFullYear()}-${String(hoy.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function FacturacionPanel() {
  const [periodo, setPeriodo] = useState(mesActual());
  const [fechaFactura, setFechaFactura] = useState("");
  const { data, isLoading, error } = useBillingSummary(periodo, fechaFactura || null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-sm flex items-center gap-2">
          Periodo:
          <input
            type="month"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value || mesActual())}
            className="px-2 py-1 text-sm rounded-md border"
          />
        </label>
        <label className="text-sm flex items-center gap-2">
          Fecha de facturación:
          <input
            type="date"
            value={fechaFactura}
            max={new Date().toLocaleDateString("en-CA", { timeZone: "America/Mexico_City" })}
            onChange={(e) => setFechaFactura(e.target.value)}
            className="px-2 py-1 text-sm rounded-md border"
          />
        </label>
        {fechaFactura && (
          <button
            className="text-xs underline text-muted-foreground"
            onClick={() => setFechaFactura("")}
          >
            usar el cierre del periodo
          </button>
        )}
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Cargando…</div>}
      {error && (
        <div className="text-sm text-destructive">
          No se pudo obtener: {(error as Error).message}
        </div>
      )}
      {data && <Desglose data={data} />}

      <MargenCard />
    </div>
  );
}

function Desglose({ data }: { data: BillingSummary }) {
  return (
    <>
      {data.avisos.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <h3 className="text-sm font-medium text-amber-700 dark:text-amber-500">
            Falta información para cerrar la factura
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc pl-5">
            {data.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="text-sm font-medium">Lo que se factura · {data.periodo}</h2>

        <table className="w-full text-sm">
          <tbody>
            <Renglon
              concepto="Servidor"
              detalle={
                data.infra.medido
                  ? `${formatBytes(data.infra.salida_bytes)} de salida de ${formatBytes(
                      data.infra.incluido_bytes,
                    )} · renta ${formatMoneda(
                      data.infra.renta,
                      data.infra.moneda,
                    )} + excedente ${formatMoneda(data.infra.excedente, data.infra.moneda)} · ${
                      data.infra.alcance
                    }`
                  : "sin medición en el periodo"
              }
              importe={data.infra.costo_mxn}
            />
            <tr className="border-t">
              <td className="py-2 font-medium">Costo de operar (servidor)</td>
              <td className="py-2 text-right font-medium">
                <Importe valor={data.subtotal_mxn} />
              </td>
            </tr>
            <Renglon
              concepto={
                data.margen_pct === null ? "Margen" : `Margen (${data.margen_pct.toFixed(2)} %)`
              }
              detalle={data.margen_pct === null ? "sin configurar" : ""}
              importe={data.margen_mxn}
            />
          </tbody>
        </table>

        <TipoCambioUsado data={data} />

        <div className="flex items-baseline justify-between border-t pt-3">
          <span className="text-sm font-medium">Total a facturar</span>
          <span className="text-2xl font-semibold">
            <Importe valor={data.total_mxn} />
          </span>
        </div>
      </div>

      <ConsumoDeIa ia={data.ia} />
    </>
  );
}


/**
 * Con qué tipo de cambio se convirtió el servidor, escrito: un peso sin su
 * tipo de cambio y su fecha a la vista no se puede reproducir después.
 */
function TipoCambioUsado({ data }: { data: BillingSummary }) {
  const tc = data.tipo_cambio;
  const corte = data.fecha_factura
    ? `fecha de facturación ${fechaCorta(data.fecha_factura)}`
    : "cierre del periodo";
  if (!tc) {
    return data.infra.costo_moneda === null ? null : (
      <p className="text-xs text-muted-foreground">Sin tipo de cambio para el {corte}.</p>
    );
  }
  const origen =
    tc.fuente === "banxico" && tc.fecha_fix
      ? `FIX Banxico del ${fechaCorta(tc.fecha_fix)}`
      : `manual${tc.vigente_desde ? ` del ${new Date(tc.vigente_desde).toLocaleString("es-MX")}` : ""}${
          tc.nota ? ` («${tc.nota}»)` : ""
        }`;
  return (
    <p className="text-xs text-muted-foreground" data-testid="tipo-cambio-usado">
      Servidor convertido con {tc.par}{" "}
      {tc.rate.toLocaleString("es-MX", { maximumFractionDigits: 4 })} · {origen} · {corte}. El
      consumo de IA usa el de cada día.
    </p>
  );
}

/**
 * El consumo de IA, fuera de la suma.
 *
 * Va en su propia tarjeta y no como un renglón tachado de la tabla: un número
 * dentro de la factura se lee como parte de la factura por mucho que diga lo
 * contrario al lado.
 */
function ConsumoDeIa({ ia }: { ia: BillingSummary["ia"] }) {
  return (
    <div className="rounded-lg border border-dashed bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium">Consumo de IA · no se factura</h2>
        <span className="text-lg font-semibold text-muted-foreground">
          <Importe valor={ia.costo_mxn} />
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {formatNumber(ia.llamadas)} llamadas · {formatNumber(ia.tokens)} tokens · {ia.alcance}
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        La llave de IA la designó el cliente, así que el proveedor se lo cobra a él
        directamente. Este número está aquí para que pueda cuadrar esa factura, no para
        sumarlo a la nuestra.
      </p>
    </div>
  );
}

function Renglon({
  concepto,
  detalle,
  importe,
}: {
  concepto: string;
  detalle: string;
  importe: number | null;
}) {
  return (
    <tr className="border-t">
      <td className="py-2">
        <div>{concepto}</div>
        {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
      </td>
      <td className="py-2 text-right align-top">
        <Importe valor={importe} />
      </td>
    </tr>
  );
}

/** Un guion no es cero: es "no se pudo calcular", y se lee distinto. */
function Importe({ valor }: { valor: number | null }) {
  if (valor === null) return <span className="text-muted-foreground">—</span>;
  return <>{formatMxn(valor)}</>;
}

function MargenCard() {
  const { data } = useBillingMargin();
  const fijar = useSetBillingMargin();
  const [valor, setValor] = useState("");
  const [nota, setNota] = useState("");

  const enviar = () => {
    const pct = Number(valor);
    if (!Number.isFinite(pct) || pct < 0) return;
    fijar.mutate(
      { margin_pct: pct, notes: nota || undefined },
      {
        onSuccess: () => {
          setValor("");
          setNota("");
        },
      },
    );
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div>
        <h2 className="text-sm font-medium">Margen sobre el costo</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {data?.current
            ? `Vigente: ${data.current.margin_pct.toFixed(2)} %. Lo fijó ${
                data.current.updated_by_username ?? "alguien"
              } el ${new Date(data.current.effective_from).toLocaleDateString("es-MX")}.`
            : "Nadie lo ha configurado. Sin margen no se puede calcular el total a facturar."}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm flex flex-col gap-1">
          Nuevo margen (%)
          <input
            type="number"
            min="0"
            step="0.01"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="px-2 py-1 text-sm rounded-md border w-32"
          />
        </label>
        <label className="text-sm flex flex-col gap-1 flex-1 min-w-48">
          Nota
          <input
            type="text"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="por qué cambia"
            className="px-2 py-1 text-sm rounded-md border w-full"
          />
        </label>
        <button
          className="px-3 py-1 text-sm rounded-md border bg-card hover:bg-accent disabled:opacity-40"
          disabled={valor === "" || fijar.isPending}
          onClick={enviar}
        >
          Fijar
        </button>
      </div>

      {fijar.isError && (
        <p className="text-sm text-destructive">
          No se pudo fijar: {(fijar.error as Error).message}. Cambiar el margen es cosa de
          super usuario.
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        El margen anterior no se reescribe, se cierra: una factura vieja se re-explica con el
        margen que regía entonces.
      </p>
    </div>
  );
}
